import { Fragment, useEffect, useState } from "react";
import {
    Alert,
    FormGroup, FormText,
    Input,
    Label,
    Modal, ModalBody, ModalFooter, ModalHeader,
    Table
} from "reactstrap";
import { faClose, faDownload, faExchange, faFileExport, faFileImport, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useSelector } from "react-redux";
import { toast } from "../../../utils/toast";
import { Button } from "../Button";
import { ConfirmationModal } from "../Modal";
import { UseImportExportProps, UseImportExportReturn } from "./types";
import { readDataFromExcelFile } from "./utils";

type ImportExportModalProps = {
    title?: string;
    fileType?: "excel" | "json";
    isLoading?: boolean;
    isUpdating?: boolean;
} & UseImportExportReturn & UseImportExportProps;

const allowedFileTypes = {
    excel: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/vnd.oasis.opendocument.spreadsheet",
        "application/vnd.ms-excel.sheet.macroEnabled.12",
        "text/csv"
    ],
    json: [
        "application/json",
        "text/json",
        "text/plain"
    ]
};

const acceptedFileTypes = {
    excel: ".csv,.xlsx,.xlsm,.xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.oasis.opendocument.spreadsheet, application/vnd.ms-excel.sheet.macroEnabled.12",
    json: ".json, application/json, text/json, text/plain",
};

export const ImportExportModal = ({
    isLoading,
    isUpdating,
    title,
    fileType = "excel",
    importFields,
    onImportData,
    onExportData,
    onDownloadTemplate,
    onDeleteAll,
    errors,
    entityName,
    ...props
}: ImportExportModalProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [showAllToBeImportedItems, setShowAllToBeImportedItems] = useState(false);
    const [importedData, setImportedData] = useState<any>(null);
    const user = useSelector((state: any) => state.auth.user);
    const tenantName = user?.tenants?.length > 1 && user.tenant?.tenant_name;

    useEffect(() => {
        if (!isOpen) {
            setSelectedFile(null);
            setImportedData(null);
            props.onFileChange(null);
            setShowAllToBeImportedItems(false);
        }
    }, [isOpen]);

    const closeModal = () => setIsOpen(false);

    const onExcelFileChange = async (e) => {
        const file = e?.target?.files?.[0];

        if (file) {
            if (!allowedFileTypes[fileType].includes(file.type)) {
                toast.error(`Invalid file type. Please select a ${fileType === "excel" ? "CSV, XLSX, or XLS" : "JSON"} file.`);
                return;
            }

            // TODO add support for json file
            const data = await readDataFromExcelFile(file);

            setSelectedFile(file);
            setImportedData(data);
            props.onFileChange(data);
        }
    };

    const onFileChange = (e) => {
        if (fileType === "excel") {
            onExcelFileChange(e);
        }
    };

    const handleImport = async () => {
        await onImportData();
        closeModal();
    };

    const handleExport = async () => {
        if (!onExportData) return;

        await onExportData();

        closeModal();
    };

    const invalidItemsCount = Object.keys(errors || {}).length;

    return (
        <>
            <Button color="secondary" className="text-nowrap" outline onClick={() => setIsOpen(true)} icon={faExchange}>Import / Export</Button>

            <Modal size="lg" isOpen={isOpen} toggle={closeModal} >
                <ModalHeader toggle={closeModal}>
                    {title ?? `Import / Export all ${entityName}s${tenantName ? ` (${tenantName})` : ""}`}
                </ModalHeader>

                <ModalBody>
                    <p>Download the template. The template contains the required columns to import {entityName}s.</p>

                    {importFields?.length && (
                        <ul>
                            {importFields?.filter((f) => !f.copyValueFrom)?.map((field) => (
                                <li key={field.name ?? field.label}>
                                    <strong>{field.name ?? field.label}{field.required && <span>*</span>}</strong>: {field.description}
                                </li>
                            ))}
                        </ul>
                    )}

                    <FormGroup>
                        <Label for="exampleFile">Excel sheet</Label>
                        <Input
                            id="exampleFile"
                            name="file"
                            type="file"
                            accept={acceptedFileTypes[fileType]}
                            onChange={onFileChange}
                        />

                        {selectedFile ? (
                            <FormText >
                                <div className="pt-2">
                                    <div>Filename: {selectedFile?.name}</div>
                                    <div>Filetype: {selectedFile?.type}</div>
                                    <div>Size in bytes: {selectedFile?.size}</div>
                                    <div className={invalidItemsCount ? "mb-4" : ""}>Total items: {importedData?.length}</div>

                                    {!!invalidItemsCount && (
                                        <Alert color="danger">
                                            <p className="fw-bold text-danger mb-3 fs-6">Invalid items: {invalidItemsCount}</p>
                                            <p className="mb-0">
                                                Please check the highlighted items below.
                                                Make sure the filled in countries and fruit types are already configured in form options.
                                                Invalid items will be ignored during import.
                                            </p>
                                        </Alert>
                                    )}
                                </div>
                            </FormText>
                        ) : (
                            <FormText>
                                Upload file
                            </FormText>
                        )}
                    </FormGroup>

                    {!!importedData?.length && (
                        <div className="overflow-x-auto w-100">
                            <Table size="sm">
                                <thead>
                                    <tr>
                                        {importFields?.filter((f) => !f.copyValueFrom)?.map((field) => <th key={field.name ?? field.label}>{field.name ?? field.label}</th>)}
                                    </tr>
                                </thead>

                                <tbody>
                                    {importedData?.slice(0, showAllToBeImportedItems ? importedData?.length : 10).map((i, index) => (
                                        <Fragment key={index}>
                                            <tr style={errors?.[index] ? { borderColor: "transparent" } : {}}>
                                                {importFields?.filter((f) => !f.copyValueFrom)?.map((field) => (
                                                    <td
                                                        key={(field.name ?? field.label) + index}
                                                        className="align-middle"
                                                        style={{
                                                            color: errors?.[index]
                                                                ? "red"
                                                                : "undefined"
                                                        }}
                                                        title={errors?.[index]?.[field?.label]}
                                                    >
                                                        {i[field.name ?? field.label]}
                                                    </td>
                                                ))}
                                            </tr>

                                            {errors?.[index] && (
                                                <tr>
                                                    <td colSpan={importFields?.filter((f) => !f.copyValueFrom)?.length} className="text-danger" style={{ fontSize: "11px", paddingBlock: 0 }}>
                                                        {Object.values(errors?.[index]).join(", ")}
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {importedData?.length > 10 && !showAllToBeImportedItems && (
                        <div className="text-center py-3">
                            <span onClick={() => setShowAllToBeImportedItems(true)} className="btn-link">Show all</span>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    <div className="mb-1 w-100 d-flex align-items-center justify-content-between gap-1">
                        <div className="d-flex align-items-center gap-2">
                            <Button color="danger" disabled={isLoading} onClick={() => setIsDeleteConfirmationOpen(true)} icon={faTrash} iconOnlyBreakpoint="lg">
                                Delete All
                            </Button>

                            <Button color="secondary" outline disabled={!handleExport} onClick={handleExport} icon={faFileExport} iconOnlyBreakpoint="lg">
                                Export
                            </Button>

                            <Button color="secondary" outline onClick={onDownloadTemplate} icon={faDownload} iconOnlyBreakpoint="lg">
                                Download template
                            </Button>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <Button color="light" onClick={() => setIsOpen(false)} icon={faClose}>
                                Cancel
                            </Button>

                            <Button color="primary" disabled={isLoading || isUpdating || !importedData} onClick={handleImport} icon={faFileImport}>
                                Import
                            </Button>
                        </div>
                    </div>
                </ModalFooter>
            </Modal>

            {!!onDeleteAll && (
                <ConfirmationModal
                    isOpen={isDeleteConfirmationOpen}
                    onClose={() => setIsDeleteConfirmationOpen(false)}
                    onConfirm={onDeleteAll}
                    header={`Delete all ${entityName}s${tenantName ? ` (${tenantName})` : ""}`}
                    color="danger"
                    size="md"
                >
                    Are you sure you want to delete all {entityName}s? This will delete all {entityName}s and its relations. This action cannot be undone.
                </ConfirmationModal>
            )}
        </>
    );
};
